import { useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DataContext } from "App";
import { HiMinus, HiPlus, HiOutlineChevronRight, HiOutlineHeart, HiOutlineShoppingCart } from "react-icons/hi";
import CartService from '../../services/cart';
import WishlistService from '../../services/wishlist';
import AuthService from '../../services/auth';
import ProductService from '../../services/products';
import { handleApiError, handleApiSuccess } from 'components/Handle';
import PriceAlert from 'components/PriceAlert/PriceAlert';
import SmartRecommendations from 'components/SmartRecommendations/SmartRecommendations';
import ProductCard from "components/ProductCard/ProductCard";
import { formatPrice } from "utils/formatPrice";
import { t } from "i18n";
import { storeThemeProps } from "utils/storeTheme";
import { getProductImageGallerySrcs } from "utils/productImages";
import {
  clampQuantityToStock,
  getProductStockQuantity,
  isProductInStock,
} from "utils/productStock";
import ProductReviewsSection from "./ProductReviewsSection";
import EarnCoinsHint from "components/Coins/EarnCoinsHint";
import { ModuleBuyOptions, ModuleDetails, isMenuUnavailable } from "components/Modules/ModuleWidgets";
import TryOnButton from "components/TryOn/TryOnButton";
import { noteTryOnAddToCart } from "services/tryon";
import { addonsTotal } from "services/modules";
import DealCountdown from "components/Deals/DealCountdown";
import ShareButtons from "components/Store/ShareButtons";
import NotFound from "pages/NotFound/NotFound";
import { StorePublicService } from "services/storeMode";
import { productPath, storePath } from "utils/storePaths";
import VariantSelector, { initialVariant, variantGallery } from "components/Variants/VariantSelector";
import BookingFields from "components/Listing/BookingFields";
import ProductGallery from "components/Product/ProductGallery";
import TrustRow from "components/Product/TrustRow";
import PolicyTab from "components/Product/PolicyTab";

function StarRating({ value, size = "sm" }) {
  const v = Number(value || 0);
  const full = Math.floor(v);
  const half = v - full >= 0.5 ? 1 : 0;
  const empty = Math.max(0, 5 - full - half);
  const starClass = size === "sm" ? "h-4 w-4" : "h-5 w-5";

  const Star = ({ fillPct }) => (
    <span className={`relative inline-block ${starClass}`} aria-hidden>
      <svg viewBox="0 0 20 20" className="h-full w-full text-slate-200">
        <path
          fill="currentColor"
          d="M9.999 1.667l2.574 5.214 5.756.836-4.165 4.06.984 5.735-5.149-2.707-5.149 2.707.984-5.735L1.67 7.717l5.756-.836L9.999 1.667z"
        />
      </svg>
      <span className="absolute inset-0 overflow-hidden" style={{ width: `${fillPct}%` }}>
        <svg viewBox="0 0 20 20" className="h-full w-full text-amber-500">
          <path
            fill="currentColor"
            d="M9.999 1.667l2.574 5.214 5.756.836-4.165 4.06.984 5.735-5.149-2.707-5.149 2.707.984-5.735L1.67 7.717l5.756-.836L9.999 1.667z"
          />
        </svg>
      </span>
    </span>
  );

  return (
    <div className="inline-flex items-center gap-1" aria-label={t('reviews.outOf5', { value: v })}>
      {Array.from({ length: full }).map((_, i) => (
        <Star key={`f-${i}`} fillPct={100} />
      ))}
      {half ? <Star key="h" fillPct={50} /> : null}
      {Array.from({ length: empty }).map((_, i) => (
        <Star key={`e-${i}`} fillPct={0} />
      ))}
    </div>
  );
}

// Store mode: rendered at /product/:productID (old URL -- redirects to the
// canonical /<handle>/<slug> once loaded) and at /:storeHandle/:productSlug
// (StoreProductPage passes the loaded `store`).
const Product = ({ store = null }) => {
  const urlParams = useParams();
  const { storeHandle, productSlug } = urlParams;
  const slugMode = Boolean(storeHandle && productSlug);
  const productID = slugMode ? `${storeHandle}/${productSlug}` : urlParams.productID;
  const navigate = useNavigate();
  const { products, setProducts, setCartList, wishList, setWishList, toast, setSnackbar } = useContext(DataContext);
  const [activeImgIndex, setActiveImgIndex] = useState(0);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [endedDealId, setEndedDealId] = useState(null);
  const endDeal = useCallback(() => setEndedDealId(currentProduct?.deal?.id ?? null), [currentProduct]);
  const [imgArr, setImgArr] = useState([]);
  const [amount, setAmount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [storeUnavailable, setStoreUnavailable] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [buyLoading, setBuyLoading] = useState(false);
  const [wishLoading, setWishLoading] = useState(false);
  const [wishPop, setWishPop] = useState(false);
  const [tab, setTab] = useState(0);
  // R6 food: chosen modifiers ({remove, addons}); priced again by the server.
  const [modifiers, setModifiers] = useState(null);
  useEffect(() => {
    setModifiers(null);
  }, [currentProduct?.id]);

  // Variants: the selected variant drives price / compare-at / stock / SKU /
  // gallery / description; ?variant=<id> keeps it in the URL.
  const [selectedVariant, setSelectedVariant] = useState(null);
  const variantData = currentProduct?.variant_data || null;
  const hasOptions = Boolean(variantData?.options?.length);
  const selectVariant = useCallback((v) => {
    setSelectedVariant(v);
    if (!v) return;
    const url = new URL(window.location.href);
    if (v.is_default) url.searchParams.delete("variant");
    else url.searchParams.set("variant", String(v.id));
    window.history.replaceState(window.history.state, "", url.toString());
  }, []);

  // Listing types: a service is booked (date / window / notes) through the cart.
  const listingType = currentProduct?.listing_type || "PHYSICAL";
  const isService = listingType === "SERVICE";
  const isDigital = listingType === "DIGITAL";
  const listingInfo = currentProduct?.listing_info || null;
  const [booking, setBooking] = useState({});
  useEffect(() => setBooking({}), [currentProduct?.id]);

  const [showAllBullets, setShowAllBullets] = useState(false);

  const productsRef = useRef(products);
  productsRef.current = products;

  const fetchProduct = useCallback(
    () => (slugMode ? StorePublicService.getProduct(storeHandle, productSlug) : ProductService.getProduct(productID)),
    [slugMode, storeHandle, productSlug, productID],
  );

  const mergeProductIntoCatalog = useCallback((product) => {
    if (!product?.id || typeof setProducts !== "function") return;
    setProducts((prev) => {
      if (!Array.isArray(prev) || !prev.length) return prev;
      const idx = prev.findIndex((p) => String(p.id) === String(product.id));
      if (idx === -1) return prev;
      const prevQty = getProductStockQuantity(prev[idx]);
      const nextQty = getProductStockQuantity(product);
      if (
        prevQty === nextQty &&
        prev[idx].stock_quantity === product.stock_quantity &&
        prev[idx].rating === product.rating &&
        prev[idx].review_count === product.review_count
      ) {
        return prev;
      }
      const next = [...prev];
      next[idx] = { ...next[idx], ...product };
      return next;
    });
  }, [setProducts]);

  const applyProductState = useCallback((product) => {
    if (!product) return;
    setCurrentProduct(product);
    const urlVariant = new URLSearchParams(window.location.search).get("variant");
    const v = product.variant_data ? initialVariant(product.variant_data, urlVariant) : null;
    setSelectedVariant((prev) => {
      const keep = prev && product.variant_data?.variants?.find((x) => x.id === prev.id);
      return keep || v;
    });
    const gallery = product.variant_data ? variantGallery(product.variant_data, v).map((m) => m.url) : [];
    setImgArr(gallery.length ? gallery : getProductImageGallerySrcs(product));
    setActiveImgIndex(0);
    setAmount((prev) => clampQuantityToStock(prev, product));
  }, []);

  // Selecting a colour swaps the gallery to that colour's images.
  useEffect(() => {
    if (!variantData || !selectedVariant) return;
    const gallery = variantGallery(variantData, selectedVariant).map((m) => m.url);
    if (gallery.length) {
      setImgArr(gallery);
      setActiveImgIndex(0);
    }
  }, [variantData, selectedVariant]);

  useEffect(() => {
    let cancelled = false;

    const loadProduct = async () => {
      setLoading(true);
      setNotFound(false);
      const catalog = productsRef.current;
      const localProduct =
        catalog && catalog.length > 0
          ? catalog.find((p) => (slugMode
            ? p.slug === productSlug && p.store_handle === storeHandle
            : p.id.toString() === productID))
          : null;

      if (localProduct && !cancelled) {
        applyProductState(localProduct);
      }

      try {
        const result = await fetchProduct();
        if (cancelled) return;
        if (result.success && result.product) {
          // Old /product/:id links land on the canonical store URL.
          const canonical = productPath(result.product);
          if (!slugMode && canonical !== `/product/${result.product.id}`) {
            navigate(`${canonical}${window.location.search}${window.location.hash}`, { replace: true });
            return;
          }
          applyProductState(result.product);
          mergeProductIntoCatalog(result.product);
        } else if (result.unavailable) {
          setStoreUnavailable(true);
        } else if (slugMode) {
          // Store mode: an unknown product slug is the normal 404 page.
          setNotFound(true);
        } else if (!localProduct) {
          navigate("/", { replace: true });
        }
      } catch (error) {
        console.error("Error loading product details:", error);
        if (!localProduct && !cancelled) {
          navigate("/", { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProduct();
    return () => {
      cancelled = true;
    };
  }, [productID, slugMode, storeHandle, productSlug, fetchProduct, navigate, applyProductState, mergeProductIntoCatalog]);

  useEffect(() => {
    if (!productID) return;

    const refreshInventory = async () => {
      const result = await fetchProduct();
      if (result.success && result.product) {
        applyProductState(result.product);
        mergeProductIntoCatalog(result.product);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") refreshInventory();
    };

    window.addEventListener("focus", refreshInventory);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", refreshInventory);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [productID, fetchProduct, applyProductState, mergeProductIntoCatalog]);

  const changeAmount = (delta) => {
    const maxQty = getProductStockQuantity(currentProduct);
    if (maxQty <= 0) return;
    setAmount((prev) => clampQuantityToStock(prev + delta, currentProduct));
  };

  const addToCart = async () => {
    if (!AuthService.isAuthenticated()) {
      (toast?.error || ((msg) => setSnackbar({ ...handleApiError(msg), open: true })))(t('productPage.loginForCart'));
      return;
    }

    if (!currentProduct?.id) return;
    if (isService && (!booking.date || !booking.window)) {
      (toast?.error || ((m) => setSnackbar({ ...handleApiError(m), open: true })))(t('listing.chooseDateFirst'));
      return;
    }

    setCartLoading(true);
    try {
      const result = await CartService.addToCart(currentProduct.id, amount, currentProduct, modifiers, cartExtra);
      if (result.success) {
        noteTryOnAddToCart(currentProduct.id); // R8: count only, if try-on was opened

        const cartResult = await CartService.getCart();
        if (cartResult.success) {
          setCartList(cartResult.cartItems);
        }
        const msg =
          result.mode === 'updated' ? t('productPage.qtyUpdated') : `${t('productPage.added')} ✅`;
        (toast?.success || ((m) => setSnackbar({ ...handleApiSuccess(m), open: true })))(msg);
      } else {
        (toast?.error || ((m) => setSnackbar({ ...handleApiError(m), open: true })))(
          result.error || t('productPage.wrong')
        );
      }
    } catch (error) {
      console.error('Add to cart:', error);
      (toast?.error || ((m) => setSnackbar({ ...handleApiError(m), open: true })))(t('productPage.addFailed'));
    } finally {
      setCartLoading(false);
    }
  };

  const buyNow = async () => {
    if (!AuthService.isAuthenticated()) {
      (toast?.error || ((msg) => setSnackbar({ ...handleApiError(msg), open: true })))(t('productPage.loginToContinue'));
      return;
    }
    if (!currentProduct?.id || !inStock) return;
    if (isService && (!booking.date || !booking.window)) {
      (toast?.error || ((m) => setSnackbar({ ...handleApiError(m), open: true })))(t('listing.chooseDateFirst'));
      return;
    }

    setBuyLoading(true);
    try {
      const result = await CartService.buyNow(currentProduct, navigate, amount, modifiers, cartExtra);
      if (result.success) {
        const cartResult = await CartService.getCart();
        if (cartResult.success) {
          setCartList(cartResult.cartItems);
        }
        (toast?.success || ((m) => setSnackbar({ ...handleApiSuccess(m), open: true })))(t('productPage.headingCheckout'));
      } else {
        (toast?.error || ((m) => setSnackbar({ ...handleApiError(m), open: true })))(
          result.error || t('productPage.checkoutFailed')
        );
      }
    } catch (error) {
      console.error('Buy now:', error);
      (toast?.error || ((m) => setSnackbar({ ...handleApiError(m), open: true })))(t('productPage.buyFailed'));
    } finally {
      setBuyLoading(false);
    }
  };

  const toggleWishlist = async () => {
    if (!currentProduct) return;

    const existingWishlistItem = wishList.find(item => item.product?.id === currentProduct.id);
    if (!AuthService.isAuthenticated()) {
      const updated = existingWishlistItem
        ? wishList.filter(item => item.product?.id !== currentProduct.id)
        : [...wishList, { product: currentProduct }];
      setWishList(updated);
      (toast?.success || ((msg) => setSnackbar({ ...handleApiSuccess(msg), open: true })))(
        existingWishlistItem ? t('productPage.removed') : `${t('productPage.saved')} ❤️`
      );
      return;
    }

    if (existingWishlistItem) {
      const result = await WishlistService.removeFromWishlist(existingWishlistItem.id);
      if (result.success) {
        setWishList(prev => prev.filter(item => item.product?.id !== currentProduct.id));
        (toast?.success || ((msg) => setSnackbar({ ...handleApiSuccess(msg), open: true })))(t('productPage.removed'));
      } else {
        (toast?.error || ((msg) => setSnackbar({ ...handleApiError(msg), open: true })))(result.error || t('productPage.wrong'));
      }
      return;
    }

    const result = await WishlistService.addToWishlist(currentProduct.id);
    if (result.success) {
      setWishList(prev => [...prev, result.wishlistItem]);
      (toast?.success || ((msg) => setSnackbar({ ...handleApiSuccess(msg), open: true })))(`${t('productPage.saved')} ❤️`);
    } else {
      (toast?.error || ((msg) => setSnackbar({ ...handleApiError(msg), open: true })))(result.error || t('productPage.wrong'));
    }
  }

  const isWished = Boolean(currentProduct && wishList.some(item => item.product?.id === currentProduct.id));
  const variant = hasOptions ? selectedVariant : null;
  const cartExtra = {
    ...(variant && !variant.is_default ? { variantId: variant.id } : {}),
    ...(isService ? { booking } : {}),
  };
  const productTitle = variant?.title || currentProduct?.name || currentProduct?.title || t('productPage.details');
  // A variant's exact stock is only sent when it is low (<= 5); otherwise it
  // is "in stock" without a number (quantity is then capped by the server).
  const stockProduct = variant
    ? { ...currentProduct, stock_quantity: variant.stock_level === "out" ? 0 : (variant.stock ?? 99) }
    : currentProduct;
  const stockCount = getProductStockQuantity(stockProduct);
  const showStockCount = (!variant || variant.stock != null) && listingType === "PHYSICAL";
  // R6: a food item the restaurant switched off can't be bought right now.
  const inStock = isProductInStock(stockProduct) && !isMenuUnavailable(currentProduct);
  const atMaxQuantity = inStock && amount >= stockCount;

  // Countdown deal: the server-sent deal price applies until ends_at; at 0 the
  // price reverts in place (the server stops applying it at the same moment).
  // Deals price the default variant only.
  const dealForLine = !variant || variant.is_default ? currentProduct?.deal : null;
  const activeDeal = dealForLine && endedDealId !== dealForLine.id ? dealForLine : null;
  const basePrice = variant ? (activeDeal ? null : variant.price) : null;
  const price = Number((activeDeal ? activeDeal.deal_price : (basePrice ?? currentProduct?.price)) || 0);
  const unitPrice = price + addonsTotal(currentProduct?.attributes, modifiers);
  const variantCompare = variant ? Number(variant.compare_at_price || 0) : 0;
  // Never an invented "was" price: only a real compare-at (deal / variant / product).
  const original = activeDeal
    ? Number(activeDeal.original_price)
    : variant
    ? variantCompare
    : (currentProduct?.original_price ? Number(currentProduct.original_price) : 0);
  const bullets = (selectedVariant?.bullets || []).slice(0, 6);
  const limitedStock = showStockCount && stockCount > 0 && stockCount <= 5;
  const hasDiscount = original > price && price > 0;
  const savings = hasDiscount ? Number((original - price).toFixed(2)) : 0;
  const discountPct = hasDiscount ? Math.round((savings / original) * 100) : 0;

  const ratingValue = Number(currentProduct?.rating) > 0
    ? Math.round(Number(currentProduct.rating) * 10) / 10
    : 0;
  const reviewCount = Number(currentProduct?.review_count) || 0;

  const related = useMemo(() => {
    if (!currentProduct?.id || !Array.isArray(products)) return [];
    const sameCategory = products.filter((p) => p?.id !== currentProduct.id && p?.category && p.category === currentProduct.category);
    const fallback = products.filter((p) => p?.id !== currentProduct.id);
    const list = sameCategory.length ? sameCategory : fallback;
    return list.slice(0, 6);
  }, [products, currentProduct]);

  if (notFound) return <NotFound />;

  if (storeUnavailable) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-2 p-6">
        <div className="w-full max-w-md rounded-[28px] bg-white p-8 shadow-xl text-center">
          <h2 className="text-xl font-bold text-slate-800 mb-2">{t('productPage.storeUnavailable')}</h2>
          <p className="text-slate-500 mb-6">{t('productPage.checkLater')}</p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="rounded-full bg-amber-600 px-6 py-2.5 text-white font-semibold hover:bg-amber-700 transition"
          >
            {t('confirmation.backHome')}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-2 p-6">
        <div className="w-full max-w-6xl rounded-[28px] bg-white p-8 shadow-xl">
          <div className="animate-pulse grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="h-[500px] rounded-[30px] bg-slate-200" />
            <div className="space-y-4">
              <div className="h-10 w-3/4 rounded-full bg-slate-200" />
              <div className="h-6 w-1/3 rounded-full bg-slate-200" />
              <div className="h-20 rounded-[24px] bg-slate-200" />
              <div className="h-44 rounded-[24px] bg-slate-200" />
              <div className="h-14 rounded-full bg-slate-200" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // R4.5: the store's theme covers only this store's own blocks (breadcrumb,
  // buy box, tabs, sticky CTA) -- never related/recommended cards, which may
  // belong to other stores.
  const themeProps = storeThemeProps(currentProduct?.store_theme_preset, currentProduct?.store_theme_accent);

  return (
    <div className="bg-surface-2">
      {currentProduct && (
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-24 md:pb-12">
          <div className="py-4" {...themeProps}>
            <nav aria-label={t('productPage.breadcrumb')} className="flex items-center gap-2 text-sm">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="text-slate-600 transition hover:text-amber-600"
              >
                {t('nav.home')}
              </button>
              <HiOutlineChevronRight className="h-4 w-4 text-slate-400 rtl:-scale-x-100" aria-hidden />
              {store ? (
                <button
                  type="button"
                  onClick={() => navigate(storePath(store.handle))}
                  className="max-w-[180px] truncate text-slate-600 transition hover:text-amber-600"
                  data-testid="breadcrumb-store"
                >
                  {store.name}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate("/shop")}
                  className="text-slate-600 transition hover:text-amber-600"
                >
                  {t('nav.shop')}
                </button>
              )}
              <HiOutlineChevronRight className="h-4 w-4 text-slate-400 rtl:-scale-x-100" aria-hidden />
              <span className="max-w-[260px] truncate font-semibold text-slate-900">{productTitle}</span>
            </nav>
          </div>

          {/* Top: Gallery + right column (Part C order) */}
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]" data-testid="store-theme-scope" {...themeProps}>
            <div className="min-w-0 rounded-2xl border border-border bg-surface p-3 sm:p-5">
              <ProductGallery images={imgArr} alt={productTitle} active={activeImgIndex} onChange={setActiveImgIndex} />
            </div>

            <div className="min-w-0 space-y-4 lg:sticky lg:top-24 lg:self-start" data-testid="buy-box">
              {(store || currentProduct.store_handle) && (
                <button type="button" onClick={() => navigate(storePath(store?.handle || currentProduct.store_handle))}
                  className="flex max-w-full items-center gap-2 text-sm text-muted hover:text-text" data-testid="sold-by">
                  {store?.logo_url ? (
                    <img src={store.logo_url} alt="" className="h-6 w-6 shrink-0 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary-ink" aria-hidden>
                      {(store?.name || currentProduct.store_handle || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="truncate">{t('pdp.soldBy')} <span className="font-semibold text-text">{store?.name || currentProduct.store_handle}</span></span>
                </button>
              )}

              <h1 className="break-words text-2xl font-extrabold tracking-tight text-text sm:text-3xl">{productTitle}</h1>

              <div className="flex flex-wrap items-center gap-3">
                <button type="button" onClick={() => { setTab(2); document.getElementById('product-tabs')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="inline-flex items-center gap-2 text-sm text-muted hover:text-text" data-testid="rating-link">
                  <StarRating value={ratingValue} size="sm" />
                  <span>{t('product.reviewCount', { count: reviewCount })}</span>
                </button>
                {currentProduct.trending_badge && (
                  <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary-ink" data-testid="trending-badge">
                    🔥 {t('pdp.trending')}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-testid="price-row">
                <span className="text-3xl font-extrabold text-text" data-testid="pdp-price">{formatPrice(price)}</span>
                {hasDiscount && <span className="text-base text-muted line-through" data-testid="pdp-compare">{formatPrice(original)}</span>}
                {hasDiscount && <span className="text-sm font-semibold text-success">{t('pdp.off', { pct: discountPct })}</span>}
              </div>

              {activeDeal && <DealCountdown endsAt={activeDeal.ends_at} onEnd={endDeal} />}
              <EarnCoinsHint amount={unitPrice * amount} />

              {bullets.length > 0 && (
                <div>
                  <p className="mb-1 text-sm font-semibold text-text">{t('pdp.keyFeatures')}</p>
                  <ul className="list-disc space-y-1 ps-5 text-sm text-text" data-testid="key-features">
                    {(showAllBullets ? bullets : bullets.slice(0, 3)).map((b) => <li key={b}>{b}</li>)}
                  </ul>
                  {bullets.length > 3 && (
                    <button type="button" className="mt-1 text-xs font-semibold text-primary-ink" onClick={() => setShowAllBullets((v) => !v)}>
                      {showAllBullets ? t('pdp.seeLess') : t('pdp.seeMore')}
                    </button>
                  )}
                </div>
              )}

              {(isService || isDigital) && listingInfo && (
                <div className="rounded-2xl border border-border bg-surface-2 p-3 text-sm text-text" data-testid="listing-info">
                  {isService ? (
                    <ul className="space-y-1">
                      <li>{t(`listing.mode.${listingInfo.service_mode || 'at_vendor'}`)}</li>
                      {listingInfo.service_duration && <li>{t('listing.durationValue', { value: listingInfo.service_duration })}</li>}
                      {listingInfo.service_cities?.length > 0 && <li>{t('listing.citiesValue', { value: listingInfo.service_cities.join(', ') })}</li>}
                    </ul>
                  ) : (
                    <p>{t(`listing.deliveredAs.${listingInfo.digital_kind || 'file'}`)}</p>
                  )}
                </div>
              )}
              {listingInfo?.safety?.length > 0 && (
                <div className="rounded-2xl border border-warning/40 bg-warning/10 p-3 text-sm text-text" role="note" data-testid="safety-guidance">
                  {listingInfo.safety.includes('involves_minors') && <p>{t('listing.safety.minors')}</p>}
                  {listingInfo.safety.includes('home_visit') && <p>{t('listing.safety.homeVisit')}</p>}
                </div>
              )}

              {hasOptions && (
                <div>
                  <VariantSelector variantData={variantData} selected={selectedVariant} onSelect={selectVariant} packageMode={isService} />
                  {variant?.sku && <p className="mt-2 text-xs text-muted" data-testid="variant-sku">{t('variants.sku', { sku: variant.sku })}</p>}
                </div>
              )}
              {isService && <BookingFields value={booking} onChange={setBooking} info={listingInfo} />}

              <ModuleBuyOptions
                product={currentProduct}
                modifiers={modifiers}
                onModifiersChange={setModifiers}
                onReorder={async (usual) => {
                  if (!AuthService.isAuthenticated()) return;
                  const res = await CartService.addToCart(currentProduct.id, 1, currentProduct, usual);
                  if (res.success) {
                    const cartResult = await CartService.getCart();
                    if (cartResult.success) setCartList(cartResult.cartItems);
                    navigate("/cart");
                  } else {
                    (toast?.error || ((m) => setSnackbar({ ...handleApiError(m), open: true })))(res.error);
                  }
                }}
              />

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid="stock-line">
                <span className={`font-semibold ${inStock ? 'text-success' : 'text-danger'}`}>
                  {inStock ? t('productPage.inStock') : t('productPage.outOfStock')}
                </span>
                {inStock && limitedStock && (
                  <span className="rounded-full bg-danger/10 px-2.5 py-0.5 text-xs font-semibold text-danger" data-testid="limited-stock">
                    {t('pdp.limitedStock')} · {t('productPage.onlyLeft', { count: stockCount })}
                  </span>
                )}
                {currentProduct.in_carts_24h ? (
                  <span className="text-xs text-muted" data-testid="in-carts">{t('pdp.inCarts', { count: currentProduct.in_carts_24h })}</span>
                ) : null}
              </div>

              <div className="flex items-stretch gap-2">
                <div className="inline-flex shrink-0 items-center rounded-full border border-border bg-surface px-1">
                  <button type="button" onClick={() => changeAmount(-1)} className="inline-flex h-10 w-9 items-center justify-center rounded-full text-text hover:bg-surface-2" aria-label={t('productPage.decrease')}>
                    <HiMinus />
                  </button>
                  <input type="number" min={1} value={amount} readOnly className="w-9 bg-transparent text-center text-sm font-semibold text-text outline-none" aria-label={t('cart.quantity')} />
                  <button type="button" onClick={() => changeAmount(+1)} disabled={!inStock || atMaxQuantity} className="inline-flex h-10 w-9 items-center justify-center rounded-full text-text hover:bg-surface-2 disabled:opacity-40" aria-label={t('productPage.increase')}>
                    <HiPlus />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={addToCart}
                  disabled={!inStock || cartLoading || buyLoading}
                  data-testid="pdp-add"
                  className={[
                    'inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold transition active:scale-[0.98]',
                    !inStock ? 'cursor-not-allowed bg-surface-2 text-muted' : 'bg-store-cta text-white hover:bg-store-cta-hover',
                  ].join(' ')}
                >
                  <HiOutlineShoppingCart className="h-5 w-5 shrink-0" aria-hidden />
                  <span className="truncate">{cartLoading ? t('productPage.adding') : isService ? t('listing.book') : t('product.addToCart')}</span>
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (wishLoading) return;
                    setWishLoading(true);
                    setWishPop(true);
                    await toggleWishlist();
                    setWishLoading(false);
                    window.setTimeout(() => setWishPop(false), 260);
                  }}
                  aria-pressed={isWished}
                  aria-label={isWished ? t('productPage.saved') : t('productPage.addToWishlist')}
                  title={isWished ? t('productPage.saved') : t('productPage.addToWishlist')}
                  className={[
                    'inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border transition',
                    wishPop ? 'motion-reduce:animate-none animate-heart-pop' : '',
                    isWished ? 'border-danger/30 bg-danger/10 text-danger' : 'border-border bg-surface text-text hover:bg-surface-2',
                  ].join(' ')}
                >
                  <HiOutlineHeart className="h-5 w-5" aria-hidden />
                </button>
              </div>

              <button
                type="button"
                onClick={() => buyNow()}
                disabled={!inStock || buyLoading || cartLoading}
                className="w-full rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-text transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:text-muted"
              >
                {buyLoading ? t('productPage.preparing') : t('productPage.buyNow')}
              </button>

              <TryOnButton product={currentProduct} />

              <div className="flex flex-wrap items-center gap-2" data-testid="share-row">
                <ShareButtons product={currentProduct} storeName={store?.name} />
                <PriceAlert productId={currentProduct.id} currentPrice={price} productName={productTitle} compact />
              </div>

              <TrustRow policies={currentProduct.policies} listingType={listingType} />
            </div>
          </div>

          {/* Info tabs */}
          <div id="product-tabs" {...themeProps} className="mt-10 overflow-hidden rounded-2xl border border-border bg-surface">
            <div className="border-b border-border">
              <div className="flex items-center gap-2 overflow-x-auto px-4 py-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {[
                  { idx: 0, label: t('productPage.tabs.description') },
                  { idx: 1, label: t('productPage.tabs.specs') },
                  { idx: 2, label: t('productPage.tabs.reviews') },
                  { idx: 3, label: listingType === 'PHYSICAL' ? t('pdp.tabs.shipping') : t('pdp.tabs.howItWorks') },
                ].map((tb) => (
                  <button
                    key={tb.idx}
                    type="button"
                    aria-current={tab === tb.idx ? "page" : undefined}
                    onClick={() => setTab(tb.idx)}
                    className={[
                      'shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition',
                      tab === tb.idx ? 'bg-ink text-white dark:bg-primary dark:text-on-primary' : 'bg-surface-2 text-text hover:bg-border',
                    ].join(' ')}
                  >
                    {tb.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-6">
              {tab === 0 && (
                <p className="whitespace-pre-line text-sm leading-7 text-text">
                  {variant?.description || currentProduct.description || t('productPage.defaultDescription')}
                </p>
              )}
              {tab === 1 && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    [t('pdpSpecs.brand'), currentProduct.brand || '—'],
                    [t('pdpSpecs.category'), currentProduct.category || '—'],
                    [t('pdpSpecs.sku'), (variant?.sku || currentProduct.sku) || '—'],
                    [t('pdpSpecs.availability'), inStock ? t('productPage.inStock') : t('productPage.outOfStock')],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-surface-2 px-4 py-3">
                      <span className="text-sm font-semibold text-muted">{k}</span>
                      <span className="min-w-0 break-words text-end text-sm font-semibold text-text">{v}</span>
                    </div>
                  ))}
                </div>
              )}
              {tab === 2 && currentProduct?.id && (
                <ProductReviewsSection
                  productId={currentProduct.id}
                  ratingValue={ratingValue}
                  reviewCount={reviewCount}
                  onReviewSubmitted={async () => {
                    const refreshed = await ProductService.getProduct(currentProduct.id);
                    if (refreshed.success && refreshed.product) {
                      applyProductState(refreshed.product);
                      mergeProductIntoCatalog(refreshed.product);
                    }
                  }}
                />
              )}
              {tab === 3 && <PolicyTab policies={currentProduct.policies} listingType={listingType} />}
            </div>
          </div>

          {/* R6: the product's own module widgets only (vertical isolation) */}
          <div className="mt-8">
            <ModuleDetails product={currentProduct} />
          </div>

          {/* Related products (ProductCard) */}
          {related.length > 0 && (
            <div className="mt-12">
              <div className="flex items-end justify-between gap-6 mb-6">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t('productPage.alsoLike')}</h2>
                  <p className="mt-2 text-sm text-slate-500">{t('productPage.alsoLikeSub')}</p>
                </div>
              </div>
              <div className="flex gap-6 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {related.map((p) => (
                  <div key={p.id} className="min-w-[280px] max-w-[320px]">
                    <ProductCard data={p} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Keep your recommendation engine section too (if backend available) */}
          <SmartRecommendations
            productId={currentProduct.id}
            currentProduct={currentProduct}
          />
        </div>
      )}

      {/* Mobile sticky bar: price + Add to cart / Book */}
      {currentProduct && (
        <div {...themeProps} className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-surface/95 backdrop-blur md:hidden" data-testid="sticky-bar">
          <div className="mx-auto flex max-w-[1200px] items-center gap-3 px-4 py-3">
            <p className="min-w-0 truncate text-base font-extrabold text-text">{formatPrice(unitPrice * amount)}</p>
            <button
              type="button"
              onClick={addToCart}
              disabled={!inStock || cartLoading || buyLoading}
              className={[
                'ms-auto inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition active:scale-[0.98]',
                !inStock ? 'bg-surface-2 text-muted' : 'bg-store-cta text-white',
              ].join(' ')}
            >
              <HiOutlineShoppingCart className="h-[18px] w-[18px]" aria-hidden />
              {cartLoading ? t('productPage.adding') : isService ? t('listing.book') : t('productPage.add')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(Product);
